/* eslint-disable max-len */
/**
 * MSDAT AI Spotlight Engine — client-side analytics for the Spotlight panel.
 * Pure functions exposed via mixin methods for Vue 2 components.
 */

const DEFAULT_ANOMALY_THRESHOLD = 15;
const TREND_MAX_YEARS = 5;

function sourceLabel(s) {
  if (!s) return '';
  return (s.name || s.source || s.label || '').toString();
}

function sourceValue(s) {
  const v = s.value;
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  return Number(v);
}

export default {
  methods: {
    /**
     * Triangulation: max spread across data sources for the same indicator slice.
     * Uses relative difference vs the larger value when the minimum is zero (avoids divide-by-zero).
     * @param {Array<{ name?: string, source?: string, value: number }>} dataSources
     * @param {number} threshold - percent over which we flag a red anomaly
     * @returns {object|null}
     */
    calculateSpotlightAnomaly(dataSources, threshold = DEFAULT_ANOMALY_THRESHOLD) {
      if (!dataSources || dataSources.length < 2) {
        return { kind: 'insufficient', reason: 'Need at least two data sources with values to triangulate.' };
      }

      const validSources = dataSources
        .map((s) => ({ ...s, name: sourceLabel(s), value: sourceValue(s) }))
        .filter((s) => s.value !== null);

      if (validSources.length < 2) {
        return { kind: 'insufficient', reason: 'Not enough numeric values across sources for this view.' };
      }

      let maxSource = validSources[0];
      let minSource = validSources[0];
      validSources.forEach((s) => {
        if (s.value > maxSource.value) maxSource = s;
        if (s.value < minSource.value) minSource = s;
      });

      const hi = maxSource.value;
      const lo = minSource.value;
      const diffPercent = hi > 0 ? ((hi - lo) / hi) * 100 : 0;

      if (diffPercent > threshold) {
        return {
          kind: 'anomaly',
          isAnomaly: true,
          diffPercent: diffPercent.toFixed(1),
          maxSource,
          minSource,
          threshold,
        };
      }

      return {
        kind: 'consistent',
        isAnomaly: false,
        diffPercent: diffPercent.toFixed(1),
        maxSource,
        minSource,
        threshold,
      };
    },

    /**
     * Peer clusters: 1D split on the current series (median split → high / low).
     * @param {Array<{ location: string, value: number }>} peerData
     */
    calculateSpotlightClusters(peerData) {
      if (!peerData || peerData.length < 3) return null;

      const validData = peerData.filter(
        (d) => d.value !== null && d.value !== undefined && !Number.isNaN(Number(d.value)),
      );
      if (validData.length < 3) return null;

      const sorted = [...validData].sort((a, b) => a.value - b.value);
      const mid = Math.floor(sorted.length / 2);
      const lowerCluster = sorted.slice(0, mid);
      const higherCluster = sorted.slice(mid);

      const avgLower = lowerCluster.reduce((sum, item) => sum + item.value, 0) / lowerCluster.length;
      const avgHigher = higherCluster.reduce((sum, item) => sum + item.value, 0) / higherCluster.length;

      return {
        clusters: [
          {
            id: 'high',
            name: 'Higher coverage cluster',
            items: [...higherCluster].reverse(),
            average: avgHigher.toFixed(1),
          },
          {
            id: 'low',
            name: 'Lower coverage cluster',
            items: lowerCluster,
            average: avgLower.toFixed(1),
          },
        ],
      };
    },

    /**
     * Linear regression on the last N years (default 5), one-year-ahead projection.
     * @param {Array<{ year: number, value: number }>} timeSeriesData
     */
    calculateSpotlightTrend(timeSeriesData) {
      if (!timeSeriesData || timeSeriesData.length < 2) return null;

      const validData = timeSeriesData
        .filter((d) => d.value !== null && d.value !== undefined && !Number.isNaN(Number(d.value)))
        .map((d) => ({ year: Number(d.year), value: Number(d.value) }))
        .filter((d) => !Number.isNaN(d.year));

      if (validData.length < 2) return null;

      validData.sort((a, b) => a.year - b.year);
      const windowed = validData.length > TREND_MAX_YEARS
        ? validData.slice(-TREND_MAX_YEARS)
        : validData;

      const n = windowed.length;
      let sumX = 0;
      let sumY = 0;
      let sumXY = 0;
      let sumXX = 0;

      windowed.forEach((d) => {
        sumX += d.year;
        sumY += d.value;
        sumXY += d.year * d.value;
        sumXX += d.year * d.year;
      });

      const denom = n * sumXX - sumX * sumX;
      if (denom === 0) return null;

      const slope = (n * sumXY - sumX * sumY) / denom;
      const intercept = (sumY - slope * sumX) / n;

      const last = windowed[windowed.length - 1];
      const nextYear = last.year + 1;
      const projectedVal = slope * nextYear + intercept;

      let direction = 'flat';
      if (slope > 0.5) direction = 'up';
      if (slope < -0.5) direction = 'down';

      let confidence = 'low';
      if (n >= 5) confidence = 'high';
      else if (n >= 3) confidence = 'medium';

      return {
        direction,
        currentYear: last.year,
        currentValue: last.value.toFixed(1),
        nextYear,
        projectedValue: projectedVal.toFixed(1),
        confidence,
        yearsUsed: n,
      };
    },
  },
};
